import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { string, arrayOf } from 'prop-types';
import { propTypes } from '../../../../../util/types';
import { injectIntl, intlShape } from '../../../../../util/reactIntl';
import { ResponsiveImage } from '../../../../../components';
import classNames from 'classnames';

import {
  PrevArrow as ArrowLeft,
  NextArrow as ArrowRight,
} from '../../../../../components/Slider/Arrows';
import css from './ImageCarousel.module.css';

// Keyboard navigation constants
const KEY_CODE_LEFT_ARROW = 37;
const KEY_CODE_RIGHT_ARROW = 39;

/**
 * ImageCarousel component displays images in a modal view with navigation controls.
 * Supports keyboard navigation with arrow keys.
 */
const ImageCarousel = props => {
  const { rootClassName, className, images, intl, selectedImage } = props;

  // Determine default index based on selectedImage
  const initialIndex = useMemo(() => {
    if (!selectedImage) return 0;
    const index = images.findIndex(img => img.id?.uuid === selectedImage.id?.uuid);
    return index >= 0 ? index : 0;
  }, [selectedImage, images]);

  const [selectedImageIndex, setSelectedImageIndex] = useState(initialIndex);

  // Derived values
  const count = images.length;
  const naturalIndex = selectedImageIndex + 1;
  const classes = classNames(rootClassName || css.root, className);
  const isNavigationVisible = count > 1;

  /**
   * Navigate to the previous image
   */
  const prev = useCallback(() => {
    if (count < 2) return;
    setSelectedImageIndex(prevIndex => (count + prevIndex - 1) % count);
  }, [count]);

  /**
   * Navigate to the next image
   */
  const next = useCallback(() => {
    if (count < 2) return;
    setSelectedImageIndex(prevIndex => (count + prevIndex + 1) % count);
  }, [count]);

  // Keyboard navigation setup
  useEffect(() => {
    const handleKeyUp = e => {
      if (e.keyCode === KEY_CODE_LEFT_ARROW) {
        prev();
      } else if (e.keyCode === KEY_CODE_RIGHT_ARROW) {
        next();
      }
    };

    window.addEventListener('keyup', handleKeyUp);
    return () => window.removeEventListener('keyup', handleKeyUp);
  }, [prev, next]);

  // Get localized alt text for the current image
  const imageAltText = intl.formatMessage(
    { id: 'ImageCarousel.imageAltText' },
    { index: naturalIndex, count }
  );

  return (
    <div
      className={classes}
      role="region"
      aria-label={intl.formatMessage({ id: 'ImageCarousel.carouselLabel' })}
    >
      <div className={css.imageWrapper}>
        <ResponsiveImage
          className={css.image}
          alt={imageAltText}
          image={images[selectedImageIndex]}
          variants={['default']}
          sizes="(max-width: 767px) 100vw, 80vw"
        />
      </div>

      {count > 0 && (
        <span className={css.imageIndex} aria-live="polite">
          {naturalIndex}/{count}
        </span>
      )}

      {isNavigationVisible && (
        <div className={css.navButtons}>
          <ArrowLeft
            onClick={prev}
            className={css.navButton}
            aria-label={intl.formatMessage({ id: 'ImageCarousel.previousImageLabel' })}
          />
          <ArrowRight
            onClick={next}
            className={css.navButton}
            aria-label={intl.formatMessage({ id: 'ImageCarousel.nextImageLabel' })}
          />
        </div>
      )}
    </div>
  );
};

ImageCarousel.defaultProps = {
  rootClassName: null,
  className: null,
  selectedImage: null,
};

ImageCarousel.propTypes = {
  // Styling props
  rootClassName: string,
  className: string,

  // Content props
  images: arrayOf(propTypes.image).isRequired,
  selectedImage: propTypes.image,

  // i18n props
  intl: intlShape.isRequired,
};

export default injectIntl(ImageCarousel);
